import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, EMPTY, forkJoin } from 'rxjs';
import { catchError, tap, finalize, map } from 'rxjs/operators';
import { KeycloakAdminService } from '../keycloak/keycloak.service';
import { UserProfileService } from '../user/user-profile.service';
import { eligibleParams } from '../../../utils/utils';
import { ApiService } from '../api/api.service';
import { environment } from '../../../environments/environment';
import {
  IContact,
  IOrganization,
  IPagination,
  ITag,
} from '../../types/contactModule';
import { ReportChartData } from '../../pages/organization/campaigns/types/messagesTypes';
import { IEntitySegmentQuery } from '../../components/organization/segments/segment-utilities';

export interface IMessage {
  from: string;
  destination: string;
  content: string;
  countryCode: string;
  languageCode: string;
  uuid: string;
  createdBy: string;
}

interface Pageable {
  pageNumber: number;
  pageSize: number;
  offset: number;
}

interface Sort {
  empty: boolean;
  unsorted: boolean;
  sorted: boolean;
}

interface Pagination {
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number; // current page number
  numberOfElements: number; // number of elements on the current page
  pageable: Pageable;
  size: number; // equivalent to pageSize
  sort: Sort;
  totalElements: number; // total number of elements across all pages
  totalPages: number; // total number of pages
}

interface MessageResponse {
  content: any[]; // Replace `any` with a more specific type if you know the structure of each message
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: Pageable;
  size: number;
  sort: Sort;
  totalElements: number;
  totalPages: number;
}

interface MessageParams {
  page?: number;
  active?: boolean;
  size?: number;
  MessageStatus?:
    | 'Completed'
    | 'Active'
    | 'Inactive'
    | 'Pending'
    | 'Canceled'
    | 'Draft';
  messageSubject?: string;
  sortBy?:
    | 'nameAsc'
    | 'nameDesc'
    | 'createdAsc'
    | 'createdDesc'
    | 'modifiedAsc'
    | 'modifiedDesc';
}

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  userService = inject(UserProfileService);
  apiService = inject(ApiService);

  get organizationId() {
    return this.userService.organizationId;
  }

  // LOCAL CODES
  // these are the selections made in the various components to send messages to them
  selectedContacts: IContact[] = [];
  selectedOrganizations: IOrganization[] = [];
  selectedTags: ITag[] = [];
  selectedSegments: IEntitySegmentQuery[] = [];

  // functions related to the variables to send message
  updateSelectedContacts(contacts: IContact[]) {
    this.selectedContacts = contacts;
  }
  updateSelectedOrganizations(organizations: IOrganization[]) {
    this.selectedOrganizations = organizations;
  }
  updateSelectedTags(tags: ITag[]) {
    this.selectedTags = tags;
  }
  updateSelectedSegments(segments: IEntitySegmentQuery[]) {
    this.selectedSegments = segments;
  }

  // API RELATED CODES

  private baseUrl = `${environment.BASE_URL}/blackkonnectcxpengagement/api/v1`;
  private baseUrl2 = `${environment.BASE_URL}/blackkonnectcxpengagement/api`;

  private messages = new BehaviorSubject<IMessage[]>([]);
  private inActiveMessages = new BehaviorSubject<IMessage[]>([]);

  public detailedMessage = new BehaviorSubject<IMessage | null>(null);
  public loading = new BehaviorSubject<boolean>(false);

  public pagination = new BehaviorSubject<Pagination | null>(null);

  private activeMessagesPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 2,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });
  public loadingActiveMessages = new BehaviorSubject<boolean>(false);

  private inActiveMessagesPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 2,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });
  public loadingInActiveMessages = new BehaviorSubject<boolean>(false);

  // SELECTED MESSAGE DETAILS
  private selectedMessageDetails: Record<string, unknown> = {};
  updateSelectedMessage(message: Record<string, unknown>) {
    this.selectedMessageDetails = message;
  }
  get getSelectedMessageDetails() {
    return this.selectedMessageDetails;
  }

  // tab counts cache
  private activeMessagesCount = new BehaviorSubject<number>(0);
  private inactiveMessagesCount = new BehaviorSubject<number>(0);
  private archiveMessagesCount = new BehaviorSubject<number>(0);

  get getActiveMessagesCount() { return this.activeMessagesCount.asObservable(); }
  get getInactiveMessagesCount() { return this.inactiveMessagesCount.asObservable(); }
  get getArchiveMessagesCount() { return this.archiveMessagesCount.asObservable(); }

  prefetchAllCounts(): void {
    forkJoin([
      this.fetchMessagesCounts('Active'),
      this.fetchMessagesCounts('Inactive'),
      this.fetchMessagesCounts('Archive'),
    ]).subscribe({
      next: ([active, inactive, archive]: [any, any, any]) => {
        this.activeMessagesCount.next(active);
        this.inactiveMessagesCount.next(inactive);
        this.archiveMessagesCount.next(archive);
      },
      error: () => {},
    });
  }

  // reports
  private messageReport = new BehaviorSubject<any>([]);
  private loadingMessageReport = new BehaviorSubject<boolean>(false);
  private messageReportChart = new BehaviorSubject<ReportChartData | null>(
    null
  );
  private loadingMessageReportChart = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakAdminService
  ) {}

  private get headers(): HttpHeaders {
    const token = this.keycloakService.getKcAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Organisation-ID': this.organizationId,
    });
  }

  // get count for any of the messages
  // SUBSCRIBE TO THIS API CALL IN THE COMPONENT
  fetchMessagesCounts(status: 'Active' | 'Inactive' | 'Archive') {
    const query: Record<string, any> = {};

    switch (status) {
      case 'Active':
        query['status.in'] = ['Completed', 'Pending', 'Active'];
        query['archive.notEquals'] = true;
        break;
      case 'Inactive':
        query['status.in'] = ['Draft', 'Canceled'];
        query['archive.notEquals'] = true;
        break;
      case 'Archive':
        query['archive.equals'] = true;
        break;

      default:
        break;
    }

    return this.apiService.get(
      `blackkonnectcxpengagement/api/engagement-messages/count?organisationId.equals=${this.organizationId}`,
      query
    );
  }

  /** Bumped per request, so a slow response never overwrites a newer one. */
  private activeRequest = 0;
  private inactiveRequest = 0;
  /** Whether the list on screen came from a background refresh (for new-row cues). */
  lastMessagesSilent = false;

  /**
   * One page of sent, sending and scheduled messages. `silent` is the
   * background refresh: no loading state, and a failure keeps the rows shown.
   */
  fetchMessages(params?: Record<string, any>, { silent = false } = {}): void {
    if (!silent) this.loadingActiveMessages.next(true);
    const request = ++this.activeRequest;
    const updatedParams: Record<string, any> = eligibleParams(params || {});

    this.http
      .get<any>(
        `${this.baseUrl}/organization/${this.organizationId}/engagement-messages`,
        {
          headers: this.headers,
          params: updatedParams,
        }
      )
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (data: any) => {
          if (request !== this.activeRequest) return;
          const { items, itemPagination } = data;
          this.lastMessagesSilent = silent;
          this.messages.next(items);
          this.activeMessagesPaginationData.next(itemPagination);
          this.loadingActiveMessages.next(false);
        },
        error: () => {
          if (request !== this.activeRequest) return;
          this.loadingActiveMessages.next(false);
        },
      });
  }

  /**
   * Scheduled sends for the dashboard queue. Returns its own stream rather
   * than writing to the shared lists, so the Messages page is unaffected.
   */
  fetchUpcomingMessages(size = 20): Observable<IMessage[]> {
    const params: Record<string, any> = eligibleParams({
      'status.in': ['Pending'],
      'archive.notEquals': true,
      // Soonest schedule first, so an older message due soon is never cut off.
      sort: ['scheduleDate,asc', 'createdAt,desc'],
      page: 0,
      // Spring Pageable reads `size`; `pageSize` was ignored (always 20 rows).
      size,
    });

    return this.http
      .get<any>(
        `${this.baseUrl}/organization/${this.organizationId}/engagement-messages`,
        { headers: this.headers, params }
      )
      .pipe(map((data) => (data?.items ?? []) as IMessage[]));
  }

  /** Drafts and cancelled sends; `silent` as in fetchMessages. */
  /**
   * The newest message that has gone out or is going out: the dashboard's
   * "Last sent". Its own request, so it never depends on which rows a
   * summary happened to return.
   */
  fetchLastSentMessage(): Observable<IMessage | null> {
    const params: Record<string, any> = eligibleParams({
      'status.in': ['Completed', 'Active'],
      'archive.notEquals': true,
      sort: ['createdAt,desc', 'id,desc'],
      page: 0,
      size: 1,
    });

    return this.http
      .get<any>(
        `${this.baseUrl}/organization/${this.organizationId}/engagement-messages`,
        { headers: this.headers, params }
      )
      .pipe(map((data) => ((data?.items ?? [])[0] ?? null) as IMessage | null));
  }

  fetchInactiveMessages(params?: Record<string, any>, { silent = false } = {}): void {
    if (!silent) this.loadingInActiveMessages.next(true);
    const request = ++this.inactiveRequest;
    const updatedParams: Record<string, any> = eligibleParams(params || {});

    this.http
      .get<any>(
        `${this.baseUrl}/organization/${this.organizationId}/engagement-messages`,
        {
          headers: this.headers,
          params: updatedParams,
        }
      )
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (data: any) => {
          if (request !== this.inactiveRequest) return;
          const { items, itemPagination } = data;
          this.inActiveMessages.next(items);
          this.inActiveMessagesPaginationData.next(itemPagination);
          this.loadingInActiveMessages.next(false);
        },
        error: () => {
          if (request !== this.inactiveRequest) return;
          this.loadingInActiveMessages.next(false);
        },
      });
  }

  createMessage(message: any): Observable<IMessage> {
    this.loading.next(true);

    return this.http
      .post<IMessage>(
        `${this.baseUrl2}/v1/organization/${this.organizationId}/engagement-messages`,
        message,
        { headers: this.headers }
      )
      .pipe(
        tap(() => {
          this.fetchMessages({
            pageNumber: 0,
            pageSize: 10,
            'status.in': ['Completed', 'Pending', 'Active'],
            sort: 'createdAt,desc',
          });
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(() => error);
        }),
        finalize(() => this.loading.next(false))
      );
  }

  updateMessage(message: any): Observable<IMessage> {
    this.loading.next(true);

    return this.http
      .patch<IMessage>(
        `${this.baseUrl2}/${this.organizationId}/engagement-messages/${message.id}`,
        message,
        { headers: this.headers }
      )
      .pipe(
        tap(() => {
          this.fetchMessages({
            pageNumber: 0,
            pageSize: 10,
            'status.in': ['Completed', 'Pending', 'Active'],
          });
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(() => error);
        }),
        finalize(() => this.loading.next(false))
      );
  }

  updateMessageStatus(message: {
    id: string;
    uuid?: string;
    status?: string;
    archive?: boolean;
  }): Observable<IMessage> {
    this.loading.next(true);

    return this.http
      .patch<IMessage>(
        `${this.baseUrl2}/organization/${this.organizationId}/engagement-messages/${message.uuid}`,
        message,
        { headers: this.headers }
      )
      .pipe(
        tap(() => {
          this.prefetchAllCounts();
        }),
        catchError((error) => {
          this.handleError(error);
          return throwError(() => error);
        }),
        finalize(() => this.loading.next(false))
      );
  }

  deleteMessage(id: string): Observable<void> {
    return this.http
      .delete<void>(
        `${this.baseUrl2}/${this.organizationId}/engagement-messages/${id}`,
        { headers: this.headers }
      )
      .pipe(
        tap(() => {
          this.messages.next(
            this.messages.getValue().filter((message) => message.uuid !== id)
          );
          this.fetchMessages({});
          this.fetchInactiveMessages({});
        }),
        catchError(this.handleError)
      );
  }

  fetchDetailedMessage(id: string): void {
    this.loading.next(true);

    this.http
      .get<IMessage>(
        `${this.baseUrl2}/organization/${this.organizationId}/engagement-messages/${id}`,
        { headers: this.headers }
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          this.loading.next(false);
          return EMPTY;
        })
      )
      .subscribe((data) => {
        this.detailedMessage.next(data);
        this.loading.next(false);
      });
  }

  // ======================================================================================================================================
  // reports stuff
  fetchMessageReport(messageUuid: string, params?: Record<string, any>) {
    this.loadingMessageReport.next(true);

    this.http
      .get(
        `${this.baseUrl}/engagement-message-reports/organization/${this.organizationId}/message/${messageUuid}`,
        {
          headers: this.headers,
          params: params,
        }
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          this.loadingMessageReport.next(false);
          return EMPTY;
        })
      )
      .subscribe((data) => {
        this.messageReport.next(data);
        this.loadingMessageReport.next(false);
      });
  }

  fetchMessageChartReport(messageUuid: string, params?: Record<string, any>) {
    this.loadingMessageReportChart.next(true);

    this.http
      .get(
        `${this.baseUrl}/engagement-message-reports/organization/${this.organizationId}/message/${messageUuid}/charts`,
        {
          headers: this.headers,
          params: params,
        }
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          this.loadingMessageReportChart.next(false);
          return EMPTY;
        })
      )
      .subscribe((data) => {
        this.messageReportChart.next(data as ReportChartData);
        this.loadingMessageReportChart.next(false);
      });
  }

  // fetch message recipients
  fetchMessageRecipients(messageUuid: string): Observable<any> {
    this.loading.next(true);

    return this.http
      .get(
        `${this.baseUrl}/organization/${this.organizationId}/engagement-messages/${messageUuid}/receipients`,
        { headers: this.headers }
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.loading.next(false);
        })
      );
  }

  // ======================================================================================================================================
  // Message details and report pages. These return their own streams and leave
  // the shared loading flags and BehaviorSubjects alone, so one page's request
  // never puts another page into a loading state or replays a stale message.

  /** One message by uuid. */
  getMessage(messageUuid: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl2}/organization/${this.organizationId}/engagement-messages/${messageUuid}`,
      { headers: this.headers }
    );
  }

  /**
   * One page of a message's delivery receipts plus the total from the
   * X-Total-Count header (null when the header is missing). Reads the
   * receipts by message uuid, which is what the delivery counts use, and
   * falls back to the organisation-scoped report if that list is refused.
   */
  fetchMessageReportPage(
    messageUuid: string,
    params: Record<string, any> = {}
  ): Observable<{ rows: any[]; total: number | null }> {
    const query: Record<string, any> = eligibleParams({
      ...params,
      'messageUuid.equals': messageUuid,
    });
    const read = (response: { body: unknown; headers: HttpHeaders }) => {
      const body: any = response.body;
      const rows = Array.isArray(body)
        ? body
        : Array.isArray(body?.items)
        ? body.items
        : Array.isArray(body?.content)
        ? body.content
        : [];
      const header = response.headers.get('X-Total-Count');
      const total = header === null || header === '' ? NaN : Number(header);
      return { rows, total: Number.isFinite(total) ? total : null };
    };

    return this.http
      .get<any>(`${this.baseUrl2}/engagement-message-reports`, {
        headers: this.headers,
        params: query,
        observe: 'response',
      })
      .pipe(
        map(read),
        catchError(() => {
          const { ['messageUuid.equals']: _uuid, ...scoped } = query;
          return this.http
            .get<any>(
              `${this.baseUrl}/engagement-message-reports/organization/${this.organizationId}/message/${messageUuid}`,
              { headers: this.headers, params: scoped, observe: 'response' }
            )
            .pipe(map(read));
        })
      );
  }

  /** How many receipts of a message match the criteria (e.g. blackListed.equals). */
  countMessageReports(
    messageUuid: string,
    criteria: Record<string, any> = {}
  ): Observable<number> {
    return this.http
      .get<number>(`${this.baseUrl2}/engagement-message-reports/count`, {
        headers: this.headers,
        params: eligibleParams({ ...criteria, 'messageUuid.equals': messageUuid }),
      })
      .pipe(map((value) => Number(value) || 0));
  }

  /** Delivery-rate, gender and age charts for one message. */
  getMessageChartReport(messageUuid: string): Observable<ReportChartData | null> {
    return this.http
      .get<ReportChartData>(
        `${this.baseUrl}/engagement-message-reports/organization/${this.organizationId}/message/${messageUuid}/charts`,
        { headers: this.headers }
      )
      .pipe(map((data) => (data && typeof data === 'object' ? data : null)));
  }

  get getMessages(): Observable<IMessage[]> {
    return this.messages.asObservable();
  }

  get getInactiveMessages(): Observable<IMessage[]> {
    return this.inActiveMessages.asObservable();
  }

  get getDetailedMessage(): Observable<IMessage | null> {
    return this.detailedMessage.asObservable();
  }

  get getActiveMessagesPagination(): Observable<IPagination> {
    return this.activeMessagesPaginationData;
  }

  get getInActiveMessagesPagination(): Observable<IPagination> {
    return this.inActiveMessagesPaginationData;
  }

  get getLoadingActiveMessages() {
    return this.loadingActiveMessages.asObservable();
  }

  get getLoadingInActiveMessages() {
    return this.loadingInActiveMessages.asObservable();
  }

  get getMessageReport() {
    return this.messageReport.asObservable();
  }

  get getLoadingMessageReport() {
    return this.loadingMessageReport.asObservable();
  }

  get getMessageReportChart() {
    return this.messageReportChart.asObservable();
  }

  get getLoadingMessageReportChart() {
    return this.loadingMessageReportChart.asObservable();
  }

  get getLoading() {
    return this.loading.asObservable();
  }

  private handleError(err: any): Observable<never> {
    return throwError(
      () => new Error(err.message || 'An unknown error occurred')
    );
  }
}
